import { tableActionsCreator } from "app/actions/tableActions";
import { SWReactTableWrapperWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import {
    getTableColumnsConfig,
    getUrlForTableRow,
} from "app/pages/industry-analysis/category-share/CategoryShareTable/CategoryShareTableConfig";
import { CategoryShareTableContainer } from "app/pages/industry-analysis/category-share/CategoryShareTable/CategoryShareTableStyles";
import { CategoryShareTableTop } from "app/pages/industry-analysis/category-share/CategoryShareTable/CategoryShareTableTop/CategoryShareTableTop";
import type {
    ICategoryShareTableProps,
    ITableColumnSort,
} from "app/pages/industry-analysis/category-share/CategoryShareTable/CategoryShareTableTypes";
import { useTableColors } from "app/pages/industry-analysis/category-share/CategoryShareTable/useTableColors";
import type { FC } from "react";
import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { getCategoryShareParams } from "../CategoryShareUtils";
import { useCurrentStateAndParams } from "@uirouter/react";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { getSegmentOrDomainCellEnrichedAttributes } from "app/components/React/Table/cells/SegmentOrDomainCell";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import categoryService from "app/common/services/categoryService";
import { CategoryServiceUtils } from "app/services/CategoryServiceUtils";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";

const TABLE_SELECTION_PROPERTY = "Id";
const DesktopMobileShare = "DesktopMobileShare";
const noDataTitleKey = "global.nodata.notavilable";
const noDataSubTitleKey = "global.nodata.notavilable.subtitle";
const tableAPI = "/api/Market/Trends/Table";

const CategoryShareTable: FC<ICategoryShareTableProps> = (props) => {
    const {
        services,
        tableSelectionKey,
        initialSelectedRowsCount = 10,
        maxSelectedRowsCount = 10,
        clearAllSelectedRows,
        selectRows,
        segmentsModule,
    } = props;
    const { params } = useCurrentStateAndParams();
    const [sort, setSort] = useState<ITableColumnSort>({
        field: "Share",
        sortDirection: "desc",
    });
    const { webSource, category } = params;
    const categoryObject = categoryService.categoryQueryParamToCategoryObject(category);
    const { initializeTableRowsWithColor, handleRowSelectionWithColor } = useTableColors();
    const tableFilters = useMemo(
        () => getCategoryShareParams(params, GranularityNames.MONTHLY, services),
        [params, services],
    );

    const tableColumns = React.useMemo(() => {
        const excludeFields = [webSource !== devicesTypes.TOTAL && DesktopMobileShare];
        const selectedIndustryHasSegments = CategoryServiceUtils.hasSegments(categoryObject);
        return getTableColumnsConfig(sort, excludeFields, selectedIndustryHasSegments);
    }, [sort, webSource, categoryObject?.categoryHash]);

    const transformData = (data) => {
        const getUserAndAccountSegmentById = SegmentsUtils.getUserAndAccountSegmentByIdHof(segmentsModule);
        return {
            itemsCount: data.TotalCount,
            records: data.Data.map((row) => {
                const { Id: SegmentId } = row;
                const segment = getUserAndAccountSegmentById(SegmentId);
                const segmentOrDomainCellEnrichedAttributes = getSegmentOrDomainCellEnrichedAttributes(row, segment);
                return {
                    ...row,
                    ...segmentOrDomainCellEnrichedAttributes,
                    url: getUrlForTableRow(row, params),
                };
            }),
        };
    };

    const onGetData = (data: { records: any[] }) => {
        clearAllSelectedRows();
        const rowsWithColor = initializeTableRowsWithColor(data.records, initialSelectedRowsCount);
        selectRows(rowsWithColor);
    };

    const onSort = (sortDetails: ITableColumnSort) => {
        setSort(sortDetails);
    };

    return (
        <CategoryShareTableContainer>
            <SWReactTableWrapperWithSelection
                tableSelectionKey={tableSelectionKey}
                tableSelectionProperty={TABLE_SELECTION_PROPERTY}
                serverApi={tableAPI}
                tableColumns={tableColumns}
                transformData={transformData}
                getDataCallback={onGetData}
                onBeforeRowSelectionToggle={handleRowSelectionWithColor}
                onSort={onSort}
                tableOptions={{
                    longLoader: true,
                    noDataTitle: services.translate(noDataTitleKey),
                    noDataSubtitle: services.translate(noDataSubTitleKey),
                    tableSelectionTrackingParam: "domain",
                }}
                initialFilters={tableFilters}
                totalRecordsField="itemsCount"
                recordsField="records"
                cleanOnUnMount={true}
                deleteTableSelectionOnUnMount={true}
                pageIndent={1}
                maxSelectedRows={maxSelectedRowsCount}
            >
                {(topComponentProps) => (
                    <CategoryShareTableTop
                        {...topComponentProps}
                        services={services}
                        tableApiQueryParams={tableFilters}
                        sort={sort}
                    />
                )}
            </SWReactTableWrapperWithSelection>
        </CategoryShareTableContainer>
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        selectedRows: getTableSelectedItems(ownProps.tableSelectionKey)(state),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    const tableAction = tableActionsCreator(ownProps.tableSelectionKey, TABLE_SELECTION_PROPERTY);
    return {
        selectRows: (rows) => {
            dispatch(tableAction.selectRows(rows));
        },
        clearAllSelectedRows: () => {
            dispatch(tableAction.clearAllSelectedRows());
        },
    };
};

const connected = connect(mapStateToProps, mapDispatchToProps)(CategoryShareTable);
export default connected;
